import ProfileFAQVO, { ProfileFAQResponse } from '@interfaces/faq/ProfileFAQVO';

type DesignNode = {
  type?: string;
  name?: string;
  components?: DesignNode[];
  attributes?: Record<string, unknown> & { attributes?: Record<string, unknown> };
};

export type DesignFaqSectionEntry = {
  designSectionId: string;
  sectionName: string;
};

const getNodeType = (node?: DesignNode) => String(node?.type ?? node?.attributes?.type ?? '');

const getNodeAttr = (node: DesignNode | undefined, key: string) => {
  const attrs = node?.attributes;
  const value = attrs?.[key] ?? attrs?.attributes?.[key];
  return typeof value === 'string' ? value : '';
};

const hasFaqContainer = (node?: DesignNode): boolean => {
  if (!node) return false;
  if (getNodeType(node) === 'pcb-faq-container') return true;
  return (node.components || []).some(hasFaqContainer);
};

const isFaqSectionNode = (node: DesignNode): boolean => getNodeAttr(node, 'section-type') === 'FAQ' || hasFaqContainer(node);

const getSectionDisplayName = (node: DesignNode): string => {
  const customName = getNodeAttr(node, 'custom-name');
  if (customName) return customName;
  if (typeof node.name === 'string' && node.name) return node.name;
  if (isFaqSectionNode(node)) return 'FAQ';
  return '';
};

const collectFaqSections = (nodes: DesignNode[] | undefined, entries: DesignFaqSectionEntry[]) => {
  (nodes || []).forEach(node => {
    if (getNodeType(node) === 'pcb-section') {
      const sectionId = getNodeAttr(node, 'section-id');
      if (isFaqSectionNode(node) && sectionId) {
        entries.push({ designSectionId: sectionId, sectionName: getSectionDisplayName(node) });
      }
    }
    collectFaqSections(node.components, entries);
  });
};

export const parseCustomBuilderDesign = (projectData: unknown): DesignFaqSectionEntry[] => {
  if (!projectData || typeof projectData !== 'object') return [];

  try {
    let pages = (projectData as { pages?: unknown }).pages;
    if (typeof pages === 'string') pages = JSON.parse(pages);
    if (!Array.isArray(pages)) return [];

    const entries: DesignFaqSectionEntry[] = [];
    pages.forEach(page => {
      const frames = Array.isArray(page?.frames) ? page.frames : [page];
      frames.forEach((frame: { component?: DesignNode } & DesignNode) => collectFaqSections([frame?.component ?? frame].filter(Boolean), entries));
    });
    return entries;
  } catch {
    return [];
  }
};

export const extractFaqSectionIdsFromDesign = (projectData: unknown): string[] => Array.from(new Set(parseCustomBuilderDesign(projectData).map(entry => entry.designSectionId)));

export const isFaqSectionName = (sectionName: string, faqLabel = 'FAQ'): boolean => {
  const normalized = sectionName?.trim().toLowerCase() || '';
  const faqLabelLower = faqLabel.toLowerCase();
  return normalized === 'faq' || normalized === faqLabelLower || /^faq\s+\d+$/.test(normalized) || normalized.includes('frequently asked question');
};

export const isPlaceholderSectionId = (sectionId: string): boolean => sectionId.includes('section_id');

export const filterPersistedSectionIds = (sectionIds?: string[] | null): string[] => Array.from(new Set((sectionIds || []).filter(id => id && !isPlaceholderSectionId(id))));

const normalizeSectionLabel = (name: string): string =>
  name
    .replace(/^Untitled\s+/i, '')
    .trim()
    .toLowerCase();

const resolvePersistedSectionId = (designSectionId: string, sectionName: string, sections?: Array<{ id: string; name?: string }>): string => {
  if (designSectionId && !isPlaceholderSectionId(designSectionId)) {
    return designSectionId;
  }
  const label = normalizeSectionLabel(sectionName);
  if (!label) return '';
  return sections?.find(section => normalizeSectionLabel(section.name || '') === label)?.id || '';
};

const resolveFaqSectionIdsByName = (sections: Array<{ id: string; name?: string }> | undefined, faqLabel = 'FAQ'): string[] =>
  (sections || []).filter(section => isFaqSectionName(section.name || '', faqLabel)).map(section => section.id);

const resolveDesignFaqSectionIds = (designFaqSections: DesignFaqSectionEntry[] | null | undefined, sections: Array<{ id: string; name?: string }> | undefined, faqLabel = 'FAQ'): string[] => {
  const resolvedFromDesign = (designFaqSections || []).map(entry => resolvePersistedSectionId(entry.designSectionId, entry.sectionName, sections));
  const fromName = resolveFaqSectionIdsByName(sections, faqLabel);
  return Array.from(new Set([...resolvedFromDesign, ...fromName].filter(Boolean)));
};

export const isFaqSection = (sectionId: string, sectionName: string, designFaqSectionIds?: string[] | null, faqLabel = 'FAQ'): boolean => {
  if (designFaqSectionIds?.includes(sectionId)) {
    return true;
  }
  return isFaqSectionName(sectionName, faqLabel);
};

export const getCustomBuilderFaqSectionIds = (
  isCustomBuilder: boolean,
  sections: Array<{ id: string; name?: string }> | undefined,
  faqLabel = 'FAQ',
  designFaqSections?: DesignFaqSectionEntry[] | null
): string[] | undefined => {
  if (!isCustomBuilder) {
    return undefined;
  }
  const sectionIds = resolveDesignFaqSectionIds(designFaqSections, sections, faqLabel);
  return sectionIds.length ? sectionIds : undefined;
};

export const getFaqSectionPagination = (faqResponse: ProfileFAQResponse | null | undefined, sectionId?: string): { hasNext: boolean; nextCursor?: string } => {
  const sectionState = sectionId ? faqResponse?.sectionPagination?.[sectionId] : undefined;
  return {
    hasNext: Boolean(sectionState?.hasNext ?? faqResponse?.paginationVO?.hasNext),
    nextCursor: sectionState?.nextCursor ?? faqResponse?.paginationVO?.nextCursor
  };
};

export const getFaqLoadMoreCursor = (faqResponse: ProfileFAQResponse | null | undefined, sectionIds?: string[], isLoadMore?: boolean): string | undefined => {
  if (!isLoadMore) {
    return undefined;
  }
  const loadMoreSectionId = sectionIds?.length === 1 ? sectionIds[0] : undefined;
  return getFaqSectionPagination(faqResponse, loadMoreSectionId).nextCursor;
};

export const getFaqsForSectionLoadMore = (response: ProfileFAQResponse, sectionId?: string | null): ProfileFAQVO[] => {
  if (!sectionId) {
    return response.artifacts ?? [];
  }
  return response.sectionFaqs?.[sectionId] ?? (response.artifacts ?? []).filter(faq => faq.sectionId === sectionId || faq.answers?.some(answer => answer.sectionId === sectionId));
};

export const resolveFaqFetchSectionIds = (
  isCustomBuilder: boolean,
  options: { isLoadMore?: boolean; sectionId?: string | null; knownSectionIds?: string[] | null },
  sections: Array<{ id: string; name?: string }> | undefined,
  faqLabel = 'FAQ',
  designFaqSections?: DesignFaqSectionEntry[] | null
): string[] | undefined => {
  if (options.isLoadMore && options.sectionId && !isPlaceholderSectionId(options.sectionId)) {
    return [options.sectionId];
  }

  const baseIds = getCustomBuilderFaqSectionIds(isCustomBuilder, sections, faqLabel, designFaqSections);
  const merged = filterPersistedSectionIds([...(baseIds || []), ...(options.knownSectionIds || []), ...(options.sectionId ? [options.sectionId] : [])]);
  return merged.length ? merged : undefined;
};
